import { TenantCredentialId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useFeature } from "store/selectors/hooks";

export const ManageIntegrationsAxisCare: React.FC = () => {
    const isFeatureEnabled = useFeature("axisCareIntegration");
    const trpcClient = trpc.useTRPC();

    const credentialQuery = useQuery(
        trpcClient.axisCare.getCredential.queryOptions(undefined, {
            enabled: isFeatureEnabled,
        }),
    );

    const [showForm, setShowForm] = React.useState(false);
    const [siteNumberInput, setSiteNumberInput] = React.useState("");
    const [tokenInput, setTokenInput] = React.useState("");
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const storeMutation = useMutation({
        ...trpcClient.axisCare.storeCredential.mutationOptions(),
        onSuccess: () => {
            setErrorMessage(null);
            setSuccessMessage("AxisCare credentials saved");
            setShowForm(false);
            setSiteNumberInput("");
            setTokenInput("");
            credentialQuery.refetch();
        },
        onError: error => {
            setSuccessMessage(null);
            setErrorMessage(error.message || "Failed to save credentials");
        },
    });

    const deleteMutation = useMutation({
        ...trpcClient.axisCare.deleteCredential.mutationOptions(),
        onSuccess: () => {
            setSuccessMessage("AxisCare credentials removed");
            setErrorMessage(null);
            credentialQuery.refetch();
        },
        onError: error => {
            setErrorMessage(error.message || "Failed to remove credentials");
        },
    });

    const credential = credentialQuery.data;

    const handleStartEdit = React.useCallback(() => {
        setShowForm(true);
        setSiteNumberInput(credential?.siteNumber ?? "");
        setTokenInput("");
        setErrorMessage(null);
        setSuccessMessage(null);
    }, [credential?.siteNumber]);

    const handleCancel = React.useCallback(() => {
        setShowForm(false);
        setSiteNumberInput("");
        setTokenInput("");
        setErrorMessage(null);
    }, []);

    const handleSave = React.useCallback(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
        storeMutation.mutate({
            siteNumber: siteNumberInput,
            token: tokenInput,
        });
    }, [siteNumberInput, tokenInput, storeMutation]);

    const handleDelete = React.useCallback(
        async (id: TenantCredentialId) => {
            const confirmed = await fancyConfirm(
                "Remove AxisCare credentials?",
                "The Send to AxisCare action will be unavailable until new credentials are added.",
                "Yes, remove",
                "Cancel",
            );
            if (confirmed) {
                deleteMutation.mutate({ id });
            }
        },
        [deleteMutation],
    );

    if (!isFeatureEnabled) {
        return (
            <ContentFull>
                <ContentHeader>AxisCare</ContentHeader>
                <InlineBanner type="info">
                    The AxisCare integration is not enabled for this account.
                </InlineBanner>
            </ContentFull>
        );
    }

    return (
        <ContentFull>
            <ContentHeader>AxisCare</ContentHeader>
            <div className="space-y-4">
                <div>
                    Connect your AxisCare account to send home care clients to AxisCare as
                    new clients.
                </div>

                {successMessage && (
                    <InlineBanner type="success">{successMessage}</InlineBanner>
                )}
                {errorMessage && <InlineBanner type="error">{errorMessage}</InlineBanner>}

                {credentialQuery.isLoading ? (
                    <div className="flex items-center space-x-2">
                        <Spinner />
                        <span>Loading...</span>
                    </div>
                ) : credential && !showForm ? (
                    <div className="p-3 border border-gray-100 rounded space-y-2">
                        <div>
                            <div className="text-sm text-gray-500">Site number</div>
                            <div className="font-medium">{credential.siteNumber}</div>
                        </div>
                        <div className="flex space-x-2 pt-2">
                            <SecondaryButton onClick={handleStartEdit}>
                                Update credentials
                            </SecondaryButton>
                            <SecondaryButton onClick={() => handleDelete(credential.id)}>
                                Remove
                            </SecondaryButton>
                        </div>
                    </div>
                ) : null}

                {!credential && !showForm && (
                    <PrimaryButton onClick={() => setShowForm(true)}>
                        Connect AxisCare
                    </PrimaryButton>
                )}

                {showForm && (
                    <div className="p-4 border border-gray-200 rounded space-y-4">
                        <h3 className="font-semibold text-lg">AxisCare credentials</h3>
                        <Input
                            label="Site number (the number in your AxisCare URL, e.g. 1234.axiscare.com)"
                            value={siteNumberInput}
                            onChange={e => setSiteNumberInput(e.target.value)}
                            placeholder="1234"
                        />
                        <Input
                            label="API token"
                            type="password"
                            value={tokenInput}
                            onChange={e => setTokenInput(e.target.value)}
                            placeholder="Enter your API token"
                        />
                        <div className="flex justify-end space-x-2">
                            <SecondaryButton onClick={handleCancel}>
                                Cancel
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={handleSave}
                                disabled={
                                    !siteNumberInput ||
                                    !tokenInput ||
                                    storeMutation.isPending
                                }>
                                {storeMutation.isPending && <Spinner />}
                                <span className={storeMutation.isPending ? "ml-2" : ""}>
                                    Save
                                </span>
                            </PrimaryButton>
                        </div>
                    </div>
                )}
            </div>
        </ContentFull>
    );
};
